import React from 'react'
import { sanitizeHTML } from '@aytm/lib/common/html'
import { SsoProviderData } from '../types'

type Props = {
  sso: SsoProviderData
}

// SSO branding strings carry intentional inline markup (e.g. <b>, <br>) from the
// trusted provider registry. Sanitize at render anyway (defense-in-depth): only the
// formatting tags survive, scripts are stripped — so a future DB/user-driven provider
// definition cannot turn this into an XSS sink (GAP-014).
const SSO_TEXT_TAGS = { allowedTags: ['b', 'strong', 'em', 'i'] }

export const SsoIndicator: React.FC<Props> = ({ sso }) => (
  <div className="tw:flex tw:items-center tw:gap-4">
    {sso.logoUrl &&
      (sso.logoMono ? (
        // A brand mark drawn for white pages reads as a dark smudge on this card. Where the
        // provider ships a silhouette, paint it as a mask instead: the artwork supplies the
        // shape, the card supplies the colour. An <img> cannot be recoloured from here.
        <span
          role="img"
          aria-label="SSO provider logo"
          className="tw:size-16 tw:shrink-0 tw:bg-white"
          style={{
            maskImage: `url(${sso.logoUrl})`,
            WebkitMaskImage: `url(${sso.logoUrl})`,
            maskRepeat: 'no-repeat',
            WebkitMaskRepeat: 'no-repeat',
            maskPosition: 'center',
            WebkitMaskPosition: 'center',
            maskSize: 'contain',
            WebkitMaskSize: 'contain'
          }}
        />
      ) : (
        <img alt="SSO provider logo" src={sso.logoUrl} className="tw:size-16 tw:object-cover tw:shrink-0" />
      ))}
    <div className="tw:flex tw:flex-col tw:gap-2">
      <span
        className="tw:text-sm tw:font-medium tw:text-white"
        dangerouslySetInnerHTML={{ __html: sanitizeHTML(sso.headerText, SSO_TEXT_TAGS) }}
      />
      <span
        className="tw:text-xs tw:text-white"
        dangerouslySetInnerHTML={{ __html: sanitizeHTML(sso.bodyText, SSO_TEXT_TAGS) }}
      />
    </div>
  </div>
)
